import React, { useState, useCallback, useEffect } from "react";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Toggle from "@amzn/meridian/toggle";
import Pagination from "@amzn/meridian/pagination";
import Link from "@amzn/meridian/link";
import Button from "@amzn/meridian/button";
import { format } from 'date-fns';
import { FAILED_REPORTS_PAGINATION_MONTHS } from "src/constants/app";

const _ = require('lodash');

export const FailedReportsTable = ({
    data,
    loaderState,
    onOpenToast,
    isSeparateTable = true,
    claimsData,
    title = "Failed Reports",
    onLoadMore,
    hasMore,
    loadMoreState,
    makingCall,
    vendorCode = "",
    monthsBack,
    failedReportsPaginationKey
}) => {
    const dateRangeDisplay = data?.dateRangeDisplay || `Last ${monthsBack + FAILED_REPORTS_PAGINATION_MONTHS} months`;
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationValue, setPaginationValue] = useState(10);
    const [sortColumn, setSortColumn] = useState("bucketId");
    const [sortDirection, setSortDirection] = useState("descending");
    const [showTable, setShowTable] = useState(true);
    const [showFailureDetails, setShowFailureDetails] = useState(false);

    const failedReports = data && data.failedReports ? data.failedReports : [];
    const totalItems = failedReports.length;
    const numberOfPages = Math.ceil(totalItems / paginationValue);

    const firstVisibleIndex = (currentPage - 1) * paginationValue;
    const lastVisibleIndex = firstVisibleIndex + paginationValue;

    const paginationHandler = useCallback((newPageNumber) => {
        setCurrentPage(newPageNumber);
    }, []);

    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortDirection(sortDirection);
        setSortColumn(sortColumn);
    }, []);

    const formatDate = (timestamp) => {
        if (!timestamp) return "Unknown";
        try {
            const date = timestamp > 10000000000
                ? new Date(timestamp) 
                : new Date(timestamp * 1000); 
            return format(date, 'yyyy-MM-dd');
        } catch (error) {
            console.error("Error formatting date:", error);
            return "Invalid Date";
        }
    };

    const parseKeyInfo = (bucket) => {
        try {
            if (bucket.key) {
                return JSON.parse(bucket.key);
            }
        } catch (error) {
            console.error("Error parsing bucket key:", error);
        }
        return {};
    };

    return (
        <Column width="100%" spacing="small">
            <Row width="100%" alignmentHorizontal="space-between" alignmentVertical="center">
                <div>
                    {showTable && (
                        <Text type="h1">
                            <span><strong>{title}</strong></span> (Queried these dates: {dateRangeDisplay}). {failedReportsPaginationKey != null ? `More data to load for these ${FAILED_REPORTS_PAGINATION_MONTHS} months.` : `Will now load the next ${FAILED_REPORTS_PAGINATION_MONTHS} months.`}
                        </Text>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    {showTable && (
                        <Toggle checked={showFailureDetails} onChange={setShowFailureDetails}>
                            Show Additional Failure Details
                        </Toggle>
                    )}
                    <Toggle checked={showTable} onChange={setShowTable}>
                        Show Failed Reports
                    </Toggle>
                </div>
            </Row>

            {showTable && (
                <>
                    <div className={'small'} style={{
                        overflowX: "auto",
                        maxWidth: "100%",
                        padding: "5px",
                    }}>
                        <Table
                            width="100%"
                            headerRows={1}
                            spacing="small"
                            showDividers={true}
                            showStripes={false}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSort={onSort}
                            fixHeaderRows={false}
                        >
                            <TableRow key={'header'} highlightOnHover={true}>
                                <TableCell sortColumn="bucketId" width="150px">Bucket ID</TableCell>
                                <TableCell sortColumn="vendorCode" width="120px">Vendor Code</TableCell>
                                <TableCell sortColumn="marketplaceName" width="130px">Marketplace</TableCell>
                                <TableCell sortColumn="startDate" width="110px">Start Date</TableCell>
                                <TableCell sortColumn="endDate" width="110px">End Date</TableCell>
                                <TableCell sortColumn="status" width="100px">Status</TableCell>
                                
                                {showFailureDetails && (
                                    <>
                                        <TableCell sortColumn="failureReason" width="300px" style={{ borderLeft: "2px solid #e0e0e0" }}>Failure Reason</TableCell>
                                        <TableCell width="300px">Resolution Steps</TableCell>
                                        <TableCell width="300px">Additional Information</TableCell>
                                    </>
                                )}
                            </TableRow>

                            {failedReports.slice(firstVisibleIndex, lastVisibleIndex).map((report, index) => {
                                const keyInfo = parseKeyInfo(report);

                                return (
                                    <TableRow key={report.bucketId || index} highlightOnHover={true}>
                                        <TableCell>
                                            {report.orcaUrl ? (
                                                <Link
                                                    href={report.orcaUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {report.bucketId}
                                                </Link>
                                            ) : (
                                                report.bucketId
                                            )}
                                        </TableCell>

                                        <TableCell>{vendorCode}</TableCell>
                                        <TableCell>{report.marketplaceName || keyInfo.marketplaceName || "Unknown"}</TableCell>
                                        <TableCell>{formatDate(report.startDate || keyInfo.start_date)}</TableCell>
                                        <TableCell>{formatDate(report.endDate || keyInfo.end_date)}</TableCell>
                                        <TableCell>{report.status || keyInfo.status || "Unknown"}</TableCell>

                                        {/* Failure details - show when toggle is on */}
                                        {showFailureDetails && (
                                            <>
                                                <TableCell style={{ borderLeft: "2px solid #e0e0e0" }}>
                                                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                        {report.failureReason || "Unknown"}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                                        {report.resolutionSteps || "No resolution steps available"}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                                        {report.additionalInfo || "No additional information is available"}
                                                    </div>
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </Table>
                    </div>

                    {(numberOfPages > 1 || hasMore) && (
                        <Row width="100%" alignmentHorizontal="right">
                            {numberOfPages > 1 && (
                                <Pagination
                                    showSkipArrows={true}
                                    numberOfPages={numberOfPages}
                                    currentPage={currentPage}
                                    onChange={paginationHandler}
                                />
                            )}
                            {hasMore && (
                                <Button
                                    onClick={onLoadMore}
                                    disabled={loadMoreState}
                                    type="tertiary"
                                    size="small"
                                >
                                    Load More
                                </Button>
                            )}
                        </Row>
                    )}

                    
                </>
            )}
        </Column>
    );
};
